import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { OrgAwareRouter } from '@/routes/OrgAwareRouter';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from '@/lib/query-client';
import { Layout, ToastContainer } from '@/components';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import {
  useServiceHealth,
  ServiceUnavailablePage,
  ServiceCheckingPage,
} from '@/components/ServiceUnavailable';
import { useCurrentUser } from '@/features/auth/api';
import { useAuthStore } from '@/features/auth/store';
import { AppRoutes } from '@/routes';
import { RouteFallback } from '@/routes/lazy';

const AuthInit: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: user } = useCurrentUser();
  const setUser = useAuthStore((state) => state.setUser);
  const storedUser = useAuthStore((state) => state.user);

  useEffect(() => {
    if (user && !storedUser) {
      setUser(user);
    }
  }, [user, storedUser, setUser]);

  return <>{children}</>;
};

/** Remounting the boundary on route change means an error on one page
 * doesn't strand the user forever — navigating away recovers automatically,
 * no full reload required.
 * The Suspense boundary covers lazy pages rendered outside AppShell/AdminLayout
 * (landing, auth, public profile); pages inside those shells suspend at the
 * boundary around their <Outlet /> so the chrome stays mounted. */
const RoutesWithBoundary: React.FC = () => {
  const location = useLocation();
  return (
    <ErrorBoundary key={location.pathname}>
      <React.Suspense fallback={<RouteFallback />}>
        <AppRoutes />
      </React.Suspense>
    </ErrorBoundary>
  );
};

function App() {
  const health = useServiceHealth();

  if (health === 'checking') return <ServiceCheckingPage />;
  if (health === 'down') return <ServiceUnavailablePage />;

  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
      <ToastContainer />
      <AuthInit>
        {/* BrowserRouter that keeps workspace links in this tab's organization. */}
        <OrgAwareRouter>
          <Layout>
            <RoutesWithBoundary />
          </Layout>
        </OrgAwareRouter>
      </AuthInit>
    </QueryClientProvider>
  );
}

export default App;
